import logger from '@scale/frontend-lib/logging';
import { QueryClient, useQuery } from '@tanstack/react-query-v4';
import _ from 'lodash';
import apiFetch from 'react-scale/lib/api-fetch';
import { FilterObject } from 'react-scale/dashboard.scale.com/components/SelfServe/ModelEval/ModelEvalRevamp/utils/types';
import { ICustomQueryParams } from '@scale/shared/types/corp';

// Where to find columns:
// scaleapi/server/src/expertData/providers.ts will contain a list of Providers
// You can add a Provider's information by adding the column name of the provider to the hook
// The column should be listed under the line 'column = {column_name}' in the source code

async function fetchProjectData(
  projectId: string,
  columns: string[],
  startTime: Date,
  endTime: Date,
  workerSpecialization?: string,
  maxGQAScore?: number,
  qualityFilters?: FilterObject,
  customQueryParams?: ICustomQueryParams,
) {
  try {
    const result = await apiFetch(null, `internal/expert-management/statsForProject`, {
      method: 'POST',
      body: {
        projectId,
        columns,
        startTimestamp: startTime.getTime(),
        endTimestamp: endTime.getTime(),
        workerSpecialization,
        maxGQAScore,
        // TODO move this to customQueryParams when updating the provider to use batches
        qualityFilters,
        customQueryParams,
      },
    });
    if (!result.success) {
      logger.error('Failed to fetch project data for projectId', { projectId });
      return {};
    }
    return result.data;
  } catch (err) {
    logger.error('Failed to fetch project data for projectId', { projectId });
    return {};
  }
}

export function useProjectDataProviderQuery(
  projectId: string,
  columns: string[],
  dateRange: Date[],
  enabled: boolean = true,
  workerSpecialization?: string,
  maxGQAScore?: number,
  qualityFilters?: FilterObject,
  customQueryParams?: any, // TODO type me properly from shared
) {
  const [startDate, endDate] = dateRange;
  const optionalFields = [];
  if (!_.isNil(workerSpecialization)) {
    optionalFields.push(`workerSpecialization=${workerSpecialization}`);
  }
  if (!_.isNil(maxGQAScore)) {
    optionalFields.push(`maxGQAScore=${maxGQAScore}`);
  }
  // TODO move this to customQueryParams when updating the provider to use batches
  if (!_.isNil(qualityFilters)) {
    optionalFields.push(`qualityFilters=${JSON.stringify(qualityFilters)}`);
  }
  if (!_.isNil(customQueryParams)) {
    optionalFields.push(`customQueryParams=${JSON.stringify(customQueryParams)}`);
  }

  const query = useQuery(
    [
      `projectDataProviderQuery`,
      projectId,
      columns.join(','),
      startDate,
      endDate,
      // Need to add this as optional because otherwise it adds undefined to the query key and it messes up other queries invalidation / optimistic updates
      ...optionalFields,
    ],
    () =>
      fetchProjectData(
        projectId,
        columns,
        startDate,
        endDate,
        workerSpecialization,
        maxGQAScore,
        qualityFilters,
        customQueryParams,
      ),
    {
      // Disable the query if we're loading a subset of the data and that subset hasn't loaded yet
      enabled,
    },
  );

  return {
    ...query,
    data: query.data,
  };
}

function getProjectDataProviderQueryKey(projectId: string, dateRange: Date[], columns: string[]) {
  const [startDate, endDate] = dateRange;
  return ['projectDataProviderQuery', projectId, columns.join(','), startDate, endDate];
}

export function invalidateProjectDataProviderQuery(
  projectId: string,
  // This is because we share queries between llm control center and worker performance page and those have different query clients
  queryClient: QueryClient,
  dateRange: Date[],
  columns: string[],
) {
  const projectAuditStatusByWorkerKey = getProjectDataProviderQueryKey(
    projectId,
    dateRange,
    columns,
  );
  queryClient.invalidateQueries({
    queryKey: projectAuditStatusByWorkerKey,
  });
}

export function setProjectDataProviderQueryData<T>(
  queryClient: QueryClient,
  projectId: string,
  updateFn: (oldConfig?: T) => T,
  columns: string[],
  dateRange: Date[],
) {
  const projectAuditStatusByWorkerKey = getProjectDataProviderQueryKey(
    projectId,
    dateRange,
    columns,
  );
  queryClient.setQueryData(projectAuditStatusByWorkerKey, updateFn);
}

// Returns previous state
export function optimisticUpdateProjectDataProviderQuery<T>(
  projectId: string,
  queryClient: QueryClient,
  columns: string[],
  dateRange: Date[],
  updateFn: (oldConfig?: T) => T,
): T {
  const projectAuditStatusByWorkerKey = getProjectDataProviderQueryKey(
    projectId,
    dateRange,
    columns,
  );
  const previousQualityProfileConfig = queryClient.getQueryData(projectAuditStatusByWorkerKey) as T;

  setProjectDataProviderQueryData(queryClient, projectId, updateFn, columns, dateRange);

  return previousQualityProfileConfig;
}
